
import { useEffect, useRef, useState } from 'react';
import DataTable from '../../../components/data-table/DataTable';
import SearchTable from '../../../components/search-table/SearchTable';
import { StyledCars } from './cars.styled';
import { deleteEmptyFieldsFromObject, setAllValuesToNull } from '../../../helpers/helpers';
import { useSelector, useDispatch } from 'react-redux';
import { AccountStructure } from '../../../models/accounts-structure';
import { RootState } from '../../../redux/store';
import { Skeleton } from '@mui/material';
import { CanceledError } from 'axios';
import HistoryIcon from '@mui/icons-material/History';
import ListItemIcon from '@mui/material/ListItemIcon';
// import MovingIcon from '@mui/icons-material/Moving';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import EditIcon from '@mui/icons-material/Edit';
import { setCarsSearch } from '../../../redux/features/browse';
import { Car, CarSchema, CarSearch } from '../../../models/car';
import { getCars } from '../../../services/carsService';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from "@emotion/react";
import { useErrorBoundary } from "react-error-boundary";
import { handleApiErrors } from '../../../services/errorHandling';
import { editLastBread, getLastAvailableLink } from '../../../components/seperator/Seperator.utils';
import { useGetAccountStructureQuery } from '../../../redux/api/accounts-structure.api';


interface SearchForm {
    [key: string]: string; // Index signature allowing any string key with string values
}
interface CarsProps {
    searchFormData?: SearchForm;
}

function Cars({ searchFormData }: CarsProps) {

    const dispatch = useDispatch()
    const { showBoundary } = useErrorBoundary();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const { data: carsStructure } = useGetAccountStructureQuery("רכב");
    // const carsStructure = useSelector((state: RootState) => state.structure.cars)
    const carsSearch = useSelector((state: RootState) => state.browse.carsSearch)

    const [data, setData] = useState<Car[]>([]);
    const [searchStructure, setSearchStructure] = useState<AccountStructure[]>();
    const [tableStructure, setTableStructure] = useState<AccountStructure[]>();
    const [sortKey, setSortKey] = useState<string>('id');
    const [sortMethod, setSortMethod] = useState<string>('DESC');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    const options = [
        {
            label: "עריכה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><EditIcon fontSize="small" /></ListItemIcon>,
            href: ""
        },
        {
            label: "הסטוריה",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><HistoryIcon fontSize="small" /></ListItemIcon>,
            href: "history"
        },
        // {
        //     label: "תנועות",
        //     icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><MovingIcon fontSize="small" /></ListItemIcon>,
        //     href: "journal-entries"
        // },
        {
            label: "מסמכים",
            icon: <ListItemIcon sx={{ color: theme.colors.main_color }}><ContactPageIcon fontSize="small" /></ListItemIcon>,
            href: "documents"
        },
    ];

    const carSearchForm = useForm<CarSearch>({
        defaultValues: {
            ...carsSearch,
            ...searchFormData
        }
    });
    const carSearchValues = carSearchForm.watch();
    const carSearchValuesRef = useRef(carSearchValues);


    useEffect(() => {
        setSearchStructure(carsStructure?.filter(field => field.show_search))
        setTableStructure(carsStructure?.filter(field => field.show_browse))
    }, [carsStructure]);



    useEffect(() => {
        if (sortKey && sortMethod) {
            carSearchForm.handleSubmit(onSearch)();
        }
    }, [sortKey, sortMethod]);


    useEffect(() => {
        if ((JSON.stringify(carSearchValues) !== JSON.stringify(carSearchValuesRef?.current)) && sortKey && sortMethod) {
            carSearchForm.handleSubmit(onSearch)();
            carSearchValuesRef.current = carSearchValues;
        }
    }, [carSearchValues]);



    function handleSortKeyChanged(value: string) {
        setSortKey(value);
    }

    function handleSortMethodChanged(value: string) {
        setSortMethod(value);
    }

    const onSearch: SubmitHandler<CarSearch> = (data) => {
        dispatch(setCarsSearch(data));
        getData(data);
    }



    async function getData(data: CarSearch) {

        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();
        setIsLoading(true);

        try {
            const responseData = await getCars(deleteEmptyFieldsFromObject(data), sortKey || 'id', "10", sortMethod || 'ASC', ["customData","customer.customData"], abortControllerRef.current.signal);
            const carsParse = z.array(CarSchema).parse(responseData);
            setData(carsParse || []);
            setIsLoading(false);
        } catch (error: unknown) {
            if (error instanceof CanceledError) {
                return;
            }
            handleApiErrors(error, showBoundary);
        }

    }


    function rowChosen<T extends Car>(row: T) {

        if(location.state?.breadcrumbData){
            navigate(getLastAvailableLink(location.state.breadcrumbData), {
                replace: true,
                state: {
                    ...location.state,
                    car: {...row, id: undefined, car_id: row.id},
                    document_account: {...row.customer, id: undefined, account_id: row.customer?.id},
                    breadcrumbData: location.state.breadcrumbData.slice(0, -1)
                }
            });
        }
    }

    function createState(){
        return {
            ...location.state,
        }
    }

    function createBreadcrumbData(state: any){

        const breadcrumbData = [
            {
                name: "כרטסות",
            },
            {
                name: "רכבים",
                link: "/cars",
            },
        ];

        return [...(editLastBread(state?.breadcrumbData, `/customers`, state) ?? breadcrumbData)];
    }

    
    function editSearchStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/cars/structure/search', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה חיפוש" }]
            }
        });
    }

    function editBrowseStructure(){

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState);

        navigate('/cars/structure/browse', {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה דפדוף" }]
            }
        });
    }

    function reset() {

        const values = setAllValuesToNull(carSearchForm.getValues());

        for (const key in values) {
            if (values.hasOwnProperty(key)) {
                carSearchForm.setValue(key as keyof CarSearch, values[key as keyof CarSearch]);
            }
        }
    }


    return (
        <StyledCars>
            {searchStructure ?
                <SearchTable structure={searchStructure} setStructure={editSearchStructure} reset={reset} useForm={carSearchForm} style={{ backgroundColor: "rgb(35, 48, 68)", padding: 5, marginButton: 20 }} /> :
                <Skeleton variant="rectangular" height={100} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
            {tableStructure ?
                <DataTable structure={tableStructure} setStructure={editBrowseStructure} sortKey={sortKey} setSortKey={handleSortKeyChanged} sortMethod={sortMethod} setSortMethod={handleSortMethodChanged} data={data} isLoading={isLoading} options={options} selectRow={location.state?.breadcrumbData ? true : false} onSelectRow={rowChosen} /> :
                <Skeleton variant="rectangular" height={150} animation="wave" sx={{ bgcolor: theme.colors.section_color }} />}
        </StyledCars>
    );
}

export default Cars;